import React, { useMemo, useState } from "react"
import { Box } from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { DayPicker as RdpDayPicker } from "react-day-picker"
import "react-day-picker/dist/style.css"

// Geometry of one day in the grid. react-day-picker lays the month out as a
// table, so a day is a `.rdp-cell` <td> DAY_SIZE wide (this feeds rdp's own
// --rdp-cell-size); the endpoint circle drawn inside it is CIRCLE_SIZE, and the
// in-range band's corner radius is BAND_RADIUS (half the circle).
const DAY_SIZE = 40
const CIRCLE_SIZE = 36
const BAND_RADIUS = 18

const buildCalendarSx = (theme) => {
  const primary = theme.palette.primary.main
  const band = alpha(primary, 0.12)

  const connectorBase = {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    width: DAY_SIZE,
    height: CIRCLE_SIZE,
    transform: "translate(-50%, -50%)",
    backgroundColor: band,
    zIndex: -2
  }
  const roundLeft = {
    borderTopLeftRadius: BAND_RADIUS,
    borderBottomLeftRadius: BAND_RADIUS
  }
  const roundRight = {
    borderTopRightRadius: BAND_RADIUS,
    borderBottomRightRadius: BAND_RADIUS
  }

  return {
    "--rdp-cell-size": `${DAY_SIZE}px`,
    "--rdp-accent-color": primary,
    "--rdp-background-color": "transparent",
    fontFamily: "Roboto, sans-serif",
    "& .rdp": { margin: 0 },
    "& .rdp-table": { borderCollapse: "collapse" },
    "& .rdp-cell": { padding: 0, width: `${DAY_SIZE}px` },
    // Day button holds the band (::before) and the circle (::after); force it
    // to fill the full cell width so the band connects edge-to-edge. Its own
    // background stays transparent so only the pseudo-elements show.
    "& .rdp-day": {
      position: "relative",
      // rdp's .rdp-button sets overflow:hidden + padding, which shrinks the
      // band and leaves gaps between days. Force a full-bleed content box with
      // no padding and visible overflow so bands abut edge-to-edge.
      overflow: "visible !important",
      boxSizing: "border-box",
      padding: "0 !important",
      width: `${DAY_SIZE}px !important`,
      minWidth: `${DAY_SIZE}px !important`,
      maxWidth: `${DAY_SIZE}px !important`,
      height: `${DAY_SIZE}px`,
      margin: "0 !important",
      background: "transparent !important",
      color: theme.palette.text.primary,
      // establish a stacking context so the negative-z pseudo layers sit below
      // the day number (which is a bare text node, not an element).
      isolation: "isolate",
      zIndex: 0,
      // rdp draws a square focus ring on the selected/focused day button; the
      // endpoint is a circle, so suppress the button outline (a11y focus still
      // shows via the circle).
      outline: "none !important",
      border: "none !important"
    },
    "& .rdp-day:focus, & .rdp-day:focus-visible, & .rdp-day:active": {
      outline: "none !important",
      boxShadow: "none !important"
    },
    // Circle layer (::after): behind the number, invisible by default, filled
    // for hover/endpoint.
    "& .rdp-day::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: `${CIRCLE_SIZE}px`,
      height: `${CIRCLE_SIZE}px`,
      borderRadius: "50%",
      backgroundColor: "transparent",
      zIndex: -1
    },
    // In-range band (::before), only on range days.
    "& .rdp-day_range_middle::before": connectorBase,
    // Start: band on the RIGHT half only (circle caps the left, mid connects
    // right). From cell centre to the column's right edge — exactly half a cell
    // so it abuts the next day's band without overlapping. Keep vertical
    // centring; drop the horizontal translate.
    "& .rdp-day_range_start::before": {
      ...connectorBase,
      left: "50%",
      width: DAY_SIZE / 2,
      transform: "translateY(-50%)"
    },
    // End: band on the LEFT half only.
    "& .rdp-day_range_end::before": {
      ...connectorBase,
      left: "auto",
      right: "50%",
      width: DAY_SIZE / 2,
      transform: "translateY(-50%)"
    },
    // Round a range-middle band's exposed edge at the start/end of a week row
    // (the band legitimately continues on the previous/next row).
    "& .rdp-row .rdp-cell:first-of-type .rdp-day_range_middle::before":
      roundLeft,
    "& .rdp-row .rdp-cell:last-of-type .rdp-day_range_middle::before":
      roundRight,
    // An endpoint's half-band points toward the range. When the endpoint sits at
    // the row edge on that side (a range_end on Monday, a range_start on Sunday)
    // there is no in-row neighbor to connect to, so the half-band is a stray
    // pale pill beside the circle — hide it (the circle alone marks the day).
    "& .rdp-row .rdp-cell:first-of-type .rdp-day_range_end::before": {
      display: "none"
    },
    "& .rdp-row .rdp-cell:last-of-type .rdp-day_range_start::before": {
      display: "none"
    },
    // Round where a range-middle segment ends mid-row because the neighboring
    // day is an outside-month day (its band is hidden), e.g. the 31st with the
    // next month's greyed days beside it. Uses the has(+ sibling) relational
    // check.
    "& .rdp-cell:has(.rdp-day_outside) + .rdp-cell .rdp-day_range_middle::before":
      roundLeft,
    "& .rdp-cell:has(.rdp-day_range_middle):has(+ .rdp-cell .rdp-day_outside) .rdp-day_range_middle::before":
      roundRight,
    // Same stray-pill case for endpoints whose band side faces an outside day:
    // a range_end preceded by an outside day, or a range_start followed by one,
    // has no in-month neighbor to connect to — hide the half-band.
    "& .rdp-cell:has(.rdp-day_outside) + .rdp-cell .rdp-day_range_end::before":
      {
        display: "none"
      },
    "& .rdp-cell:has(.rdp-day_range_start):has(+ .rdp-cell .rdp-day_outside) .rdp-day_range_start::before":
      {
        display: "none"
      },
    // Endpoints: solid primary circle via ::after; the button's OWN background
    // (rdp defaults it to the accent color → a blue square) must be killed.
    // Circle a day that is an endpoint OR a lone selected day, but never a
    // range-middle day: in mode="range" rdp marks EVERY in-range day `selected`,
    // so keying purely off `selected` would circle the whole band — the
    // `:not(.rdp-day_range_middle)` guard excludes the band. This also covers a
    // single-month calendar with only its own endpoint picked (selected, but no
    // range_start/end class yet because the full range isn't set).
    "& .rdp-day_range_start, & .rdp-day_range_end, & .rdp-day_selected:not(.rdp-day_range_middle)":
      {
        backgroundColor: "transparent !important",
        color: `${theme.palette.primary.contrastText} !important`,
        fontWeight: 500
      },
    // The middle band's own selected background must stay transparent too.
    "& .rdp-day_selected": { backgroundColor: "transparent !important" },
    "& .rdp-day_range_start::after, & .rdp-day_range_end::after, & .rdp-day_selected:not(.rdp-day_range_middle)::after":
      {
        backgroundColor: primary
      },
    // Hover on an enabled, non-endpoint, non-selected day: pale circle.
    "& .rdp-day:not(.rdp-day_range_start):not(.rdp-day_range_end):not(.rdp-day_selected):not([disabled]):hover::after":
      {
        backgroundColor: alpha(primary, 0.16)
      },
    // Disabled + outside days: greyed, non-interactive, no band.
    "& .rdp-day_disabled, & .rdp-day_outside": {
      color: theme.palette.text.disabled,
      opacity: 1
    },
    "& .rdp-day_outside": { pointerEvents: "none" },
    "& .rdp-day_outside::before, & .rdp-day_outside::after": { display: "none" }
  }
}

// Drive the same range_start / range_middle / range_end classes react-day-
// picker sets in range mode, so the branded band spans across two single-month
// calendars. The endpoint that this month owns renders via `selected`.
//
// Only band an actual span (end strictly after start). For a single-day range
// (start === end) return no modifiers, so just the one endpoint circle shows —
// otherwise the `{ after, before }` middle matcher degenerates and tints days
// across the whole month.
const rangeModifiers = (rangeStart, rangeEnd) =>
  rangeStart && rangeEnd && rangeEnd > rangeStart
    ? {
        range_start: rangeStart,
        range_end: rangeEnd,
        range_middle: { after: rangeStart, before: rangeEnd }
      }
    : {}

const RANGE_MODIFIER_CLASS_NAMES = {
  range_start: "rdp-day_range_start",
  range_middle: "rdp-day_range_middle",
  range_end: "rdp-day_range_end"
}

/**
 * Branded react-day-picker calendar for berry-jass.
 *
 * @param {Object} props
 * @param {"single" | "range"} [props.mode] "range" picks both endpoints in one
 *   month; "single" owns a single endpoint (pass `rangeStart`/`rangeEnd` to
 *   band a range split across two calendars). Defaults to "range".
 * @param {import("react-day-picker").Matcher | import("react-day-picker").Matcher[]} [props.disabled]
 *   react-day-picker disabled matcher(s); the caller supplies any business
 *   bounds (e.g. no future days).
 * @param {import("date-fns").Locale} [props.locale] date-fns locale.
 * @param {Date} [props.selected] mode="single" only: the endpoint this month
 *   owns.
 * @param {{ from?: Date, to?: Date }} [props.range] mode="range" only: the
 *   selected range.
 * @param {Date} [props.rangeStart] mode="single" only: range start, to drive
 *   the band.
 * @param {Date} [props.rangeEnd] mode="single" only: range end, to drive the
 *   band.
 * @param {(value: any) => void} [props.onSelect] react-day-picker onSelect; the
 *   payload is a `Date` in single mode and a `{ from, to }` range object in
 *   range mode.
 * @param {Date} [props.defaultMonth] the month shown first.
 * @param {boolean} [props.disableOutsideDays] when true, days shown from the
 *   neighboring month are greyed and not selectable (they still render).
 * @param {import("@mui/material").SxProps} [props.sx] extra styles for the
 *   wrapper.
 * @param {import("react-day-picker").DayPickerProps} [props.dayPickerProps]
 *   escape hatch: props forwarded to the underlying react-day-picker last, so
 *   they override everything the component sets (mode, month, onSelect,
 *   disabled, showOutsideDays, …). Use with care — overriding the controlled
 *   props can break selection/branding.
 */
export const DayPicker = ({
  mode = "range",
  disabled,
  locale,
  selected,
  range,
  rangeStart,
  rangeEnd,
  onSelect,
  defaultMonth,
  disableOutsideDays,
  sx,
  dayPickerProps = {}
}) => {
  const theme = useTheme()
  const [month, setMonth] = useState(
    defaultMonth || rangeStart || rangeEnd || selected || new Date()
  )

  const rangeProps =
    mode === "single"
      ? {
          selected,
          modifiers: rangeModifiers(rangeStart, rangeEnd),
          modifiersClassNames: RANGE_MODIFIER_CLASS_NAMES
        }
      : { selected: range }

  const isOutsideMonth = (day) =>
    day.getMonth() !== month.getMonth() ||
    day.getFullYear() !== month.getFullYear()

  const disabledMatchers = [
    disableOutsideDays && isOutsideMonth,
    ...(Array.isArray(disabled) ? disabled : [disabled])
  ].filter(Boolean)

  const calendarSx = useMemo(() => buildCalendarSx(theme), [theme])

  return (
    <Box sx={[calendarSx, ...(Array.isArray(sx) ? sx : [sx])]}>
      <RdpDayPicker
        {...rangeProps}
        mode={mode}
        month={month}
        onMonthChange={setMonth}
        onSelect={onSelect}
        disabled={disabledMatchers}
        locale={locale}
        weekStartsOn={1}
        fixedWeeks
        showOutsideDays
        {...dayPickerProps}
      />
    </Box>
  )
}
